import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Save, Send, AlertTriangle, StickyNote } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function mondayOf(d) {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7; // Mon=0
  date.setDate(date.getDate() - day);
  date.setHours(0, 0, 0, 0);
  return date;
}
function fmt(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function pretty(d) {
  return d.toLocaleDateString("en-US", { day: "numeric", month: "short" });
}

const STATUS_STYLES = {
  draft: "bg-neutral-100 text-neutral-700",
  pending: "bg-[#FEF7E0] text-[#B98900]",
  approved: "bg-[#E6F4EA] text-[#008060]",
  rejected: "bg-[#FCE8E6] text-[#D82C0D]",
};

export default function StaffTimesheet() {
  const { user } = useAuth();
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [projects, setProjects] = useState([]);
  const [timesheet, setTimesheet] = useState(null);
  const [rows, setRows] = useState([]); // [{ project_id, description, billable, hours: {date: val}, notes: {date: str} }]
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const weekDates = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, tRes] = await Promise.all([
        api.get("/projects"),
        api.get("/timesheets/mine", { params: { week_start: fmt(weekStart) } }),
      ]);
      setProjects(pRes.data);
      setTimesheet(tRes.data);
      // Group entries by project
      const byProject = {};
      (tRes.data.entries || []).forEach((e) => {
        const key = e.project_id;
        if (!byProject[key]) {
          byProject[key] = {
            project_id: e.project_id,
            description: e.description || "",
            billable: e.billable !== false,
            hours: {},
            notes: {},
          };
        }
        byProject[key].hours[e.date] = e.hours;
        if (e.note) byProject[key].notes[e.date] = e.note;
        // Prefer any non-empty description
        if (e.description) byProject[key].description = e.description;
      });
      const initRows = Object.values(byProject);
      if (initRows.length === 0 && pRes.data.length > 0) {
        initRows.push({
          project_id: pRes.data[0].id,
          description: "",
          billable: true,
          hours: {},
          notes: {},
        });
      }
      setRows(initRows);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart]);

  const readOnly = timesheet?.status === "pending" || timesheet?.status === "approved";

  const addRow = () => {
    if (projects.length === 0) {
      toast.error("No projects assigned to you yet.");
      return;
    }
    setRows((r) => [
      ...r,
      { project_id: projects[0].id, description: "", billable: true, hours: {}, notes: {} },
    ]);
  };
  const removeRow = (idx) => setRows((r) => r.filter((_, i) => i !== idx));
  const updateRow = (idx, patch) =>
    setRows((r) => r.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  const updateHour = (idx, date, val) => {
    const num = val === "" ? "" : Math.max(0, Math.min(24, Number(val) || 0));
    setRows((r) =>
      r.map((row, i) => {
        if (i !== idx) return row;
        const hours = { ...row.hours };
        if (num === "" || num === 0) delete hours[date];
        else hours[date] = num;
        return { ...row, hours };
      })
    );
  };
  const updateNote = (idx, date, val) => {
    setRows((r) =>
      r.map((row, i) => {
        if (i !== idx) return row;
        const notes = { ...(row.notes || {}) };
        if (!val) delete notes[date];
        else notes[date] = val;
        return { ...row, notes };
      })
    );
  };

  const buildEntries = () => {
    const entries = [];
    rows.forEach((row) => {
      const notes = row.notes || {};
      // Include any date that has hours OR a non-empty note (so notes can stand alone if hours later)
      const dates = new Set([...Object.keys(row.hours), ...Object.keys(notes)]);
      dates.forEach((date) => {
        const hours = Number(row.hours[date] || 0);
        const note = (notes[date] || "").trim();
        if (hours > 0 || note) {
          entries.push({
            project_id: row.project_id,
            date,
            hours,
            description: row.description || "",
            note,
            billable: !!row.billable,
          });
        }
      });
    });
    return entries;
  };

  const save = async (submit) => {
    const entries = buildEntries();
    if (submit && entries.length === 0) {
      toast.error("Enter at least one hour before submitting.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/timesheets", {
        week_start: fmt(weekStart),
        entries,
        submit,
      });
      setTimesheet(data);
      toast.success(submit ? "Timesheet submitted for approval" : "Draft saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  // Totals
  const rowTotals = rows.map((row) =>
    Object.values(row.hours).reduce((s, h) => s + Number(h || 0), 0)
  );
  const dayTotals = weekDates.map((d) => {
    const key = fmt(d);
    return rows.reduce((s, row) => s + Number(row.hours[key] || 0), 0);
  });
  const weekTotal = rowTotals.reduce((a, b) => a + b, 0);
  const billableTotal = rows.reduce(
    (s, r) => s + (r.billable ? Object.values(r.hours).reduce((x, h) => x + Number(h || 0), 0) : 0),
    0
  );

  return (
    <div className="p-6 md:p-10 max-w-[1400px]">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">
            Weekly Timesheet
          </div>
          <h1 className="font-display font-black text-3xl md:text-5xl leading-none">
            {pretty(weekStart)} — {pretty(addDays(weekStart, 6))}
          </h1>
          <div className="text-sm text-neutral-500 mt-2">Hi {user?.name}, log your hours for the week.</div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            data-testid="prev-week-btn"
            onClick={() => setWeekStart((d) => addDays(d, -7))}
            className="rounded-sm"
          >
            <ChevronLeft size={16} /> Prev
          </Button>
          <Button
            variant="outline"
            size="sm"
            data-testid="this-week-btn"
            onClick={() => setWeekStart(mondayOf(new Date()))}
            className="rounded-sm"
          >
            This week
          </Button>
          <Button
            variant="outline"
            size="sm"
            data-testid="next-week-btn"
            onClick={() => setWeekStart((d) => addDays(d, 7))}
            className="rounded-sm"
          >
            Next <ChevronRight size={16} />
          </Button>
        </div>
      </div>

      {/* Status banner */}
      {timesheet?.status && timesheet.status !== "draft" && (
        <div
          className={`mb-6 px-5 py-3 border ${
            timesheet.status === "rejected"
              ? "border-[#F2C6BF] bg-[#FCE8E6]"
              : timesheet.status === "approved"
              ? "border-[#B8DDC7] bg-[#E6F4EA]"
              : "border-[#F0E1A6] bg-[#FEF7E0]"
          }`}
          data-testid="timesheet-status-banner"
        >
          <div className="flex items-center gap-3 text-sm">
            <span
              className={`status-pill ${STATUS_STYLES[timesheet.status]}`}
              data-testid="timesheet-status-pill"
            >
              {timesheet.status}
            </span>
            {timesheet.status === "rejected" && (
              <span className="flex items-center gap-2 text-[#7A1D0A]">
                <AlertTriangle size={14} />
                Rejected: {timesheet.rejection_reason || "No reason given"}
              </span>
            )}
            {timesheet.status === "approved" && (
              <span className="text-[#005A44]">Approved by {timesheet.reviewed_by}</span>
            )}
            {timesheet.status === "pending" && (
              <span className="text-[#7A5B00]">Awaiting admin approval — read only.</span>
            )}
          </div>
        </div>
      )}

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <SummaryTile label="Week hours" value={weekTotal.toFixed(2)} testid="summary-week-hours" />
        <SummaryTile label="Billable" value={billableTotal.toFixed(2)} testid="summary-billable" />
        <SummaryTile label="Projects" value={rows.length} testid="summary-projects" />
        <SummaryTile
          label="Status"
          value={timesheet?.status || "draft"}
          uppercase
          testid="summary-status"
        />
      </div>

      {/* Grid */}
      <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-[900px]">
            <thead>
              <tr className="border-b border-[#E5E5E5]">
                <th className="sticky left-0 bg-white z-10 text-left text-xs uppercase tracking-wider text-neutral-500 font-medium px-4 py-3 border-r border-[#E5E5E5] w-[280px]">
                  Project / Description
                </th>
                {weekDates.map((d, i) => {
                  const isWeekend = i >= 5;
                  return (
                    <th
                      key={i}
                      className={`text-center text-xs uppercase tracking-wider font-medium px-2 py-3 border-r border-[#E5E5E5] ${
                        isWeekend ? "bg-[#FAFAF9] text-neutral-400" : "text-neutral-500"
                      }`}
                    >
                      <div>{DAY_NAMES[i]}</div>
                      <div className="font-mono-num text-[10px] mt-1">{pretty(d)}</div>
                    </th>
                  );
                })}
                <th className="text-right text-xs uppercase tracking-wider text-neutral-500 font-medium px-3 py-3 w-[100px]">
                  Total
                </th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const total = rowTotals[idx];
                return (
                  <tr key={idx} className="border-b border-[#E5E5E5]">
                    <td className="sticky left-0 bg-white z-10 border-r border-[#E5E5E5] px-4 py-3 align-top">
                      <select
                        value={row.project_id}
                        disabled={readOnly}
                        onChange={(e) => updateRow(idx, { project_id: e.target.value })}
                        data-testid={`row-project-select-${idx}`}
                        className="w-full text-sm font-medium bg-transparent focus:outline-none disabled:opacity-70"
                      >
                        {projects.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                      <input
                        type="text"
                        placeholder="Task / description"
                        value={row.description}
                        disabled={readOnly}
                        onChange={(e) => updateRow(idx, { description: e.target.value })}
                        data-testid={`row-description-${idx}`}
                        className="mt-1 w-full text-xs text-neutral-600 bg-transparent focus:outline-none"
                      />
                      <div className="mt-2 flex items-center gap-2">
                        <Switch
                          checked={!!row.billable}
                          disabled={readOnly}
                          onCheckedChange={(v) => updateRow(idx, { billable: v })}
                          data-testid={`row-billable-toggle-${idx}`}
                        />
                        <span className="text-[11px] uppercase tracking-wider text-neutral-500">
                          {row.billable ? "Billable" : "Non-billable"}
                        </span>
                      </div>
                    </td>
                    {weekDates.map((d, i) => {
                      const key = fmt(d);
                      const isWeekend = i >= 5;
                      const val = row.hours[key] ?? "";
                      const note = row.notes?.[key] || "";
                      return (
                        <td
                          key={i}
                          className={`ts-cell border-r border-[#E5E5E5] p-0 ${isWeekend ? "weekend" : ""}`}
                        >
                          <div className="relative">
                            <input
                              type="number"
                              step="0.25"
                              min="0"
                              max="24"
                              value={val}
                              disabled={readOnly}
                              placeholder="0.00"
                              onChange={(e) => updateHour(idx, key, e.target.value)}
                              data-testid={`hours-input-${idx}-${i}`}
                              className="pr-7"
                            />
                            <Popover>
                              <PopoverTrigger asChild>
                                <button
                                  type="button"
                                  title={note ? note : "Add note for this day"}
                                  data-testid={`note-btn-${idx}-${i}`}
                                  className={`absolute right-1.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-sm transition-colors ${
                                    note
                                      ? "text-[#0033CC] bg-[#EFF3FF]"
                                      : "text-neutral-300 hover:text-[#0033CC] hover:bg-neutral-100"
                                  }`}
                                >
                                  <StickyNote size={12} />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent
                                align="end"
                                className="w-72 rounded-md"
                                data-testid={`note-popover-${idx}-${i}`}
                              >
                                <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-2">
                                  Note — {pretty(d)}
                                </div>
                                <Textarea
                                  rows={4}
                                  placeholder="What did you work on today?"
                                  value={note}
                                  disabled={readOnly}
                                  onChange={(e) => updateNote(idx, key, e.target.value)}
                                  data-testid={`note-input-${idx}-${i}`}
                                  className="rounded-sm text-sm"
                                  maxLength={500}
                                />
                                <div className="mt-1 text-[10px] text-neutral-400 text-right font-mono-num">
                                  {note.length}/500
                                </div>
                              </PopoverContent>
                            </Popover>
                          </div>
                        </td>
                      );
                    })}
                    <td className="px-3 py-3 text-right font-mono-num text-sm font-medium" data-testid={`row-total-${idx}`}>
                      {total.toFixed(2)}
                    </td>
                    <td className="px-2 py-3 text-center">
                      {!readOnly && rows.length > 1 && (
                        <button
                          onClick={() => removeRow(idx)}
                          data-testid={`remove-row-btn-${idx}`}
                          className="text-neutral-400 hover:text-[#D82C0D] transition-colors text-lg leading-none"
                          title="Remove row"
                        >
                          ×
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {/* Day totals */}
              <tr className="bg-[#F3F3F2]">
                <td className="sticky left-0 bg-[#F3F3F2] z-10 px-4 py-3 border-r border-[#E5E5E5] text-xs uppercase tracking-wider text-neutral-600 font-medium">
                  Daily total
                </td>
                {dayTotals.map((t, i) => (
                  <td
                    key={i}
                    className="text-center font-mono-num text-sm font-semibold border-r border-[#E5E5E5] py-3"
                    data-testid={`day-total-${i}`}
                  >
                    {t.toFixed(2)}
                  </td>
                ))}
                <td className="px-3 py-3 text-right font-mono-num text-sm font-bold" data-testid="week-total">
                  {weekTotal.toFixed(2)}
                </td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
        {!readOnly && (
          <div className="p-3 border-t border-[#E5E5E5]">
            <button
              onClick={addRow}
              data-testid="add-project-row-btn"
              className="text-sm text-[#0033CC] hover:text-[#002299] transition-colors font-medium"
            >
              + Add project row
            </button>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-end">
        <Button
          variant="outline"
          className="rounded-sm h-11"
          onClick={() => save(false)}
          disabled={saving || readOnly}
          data-testid="save-draft-btn"
        >
          <Save size={16} className="mr-2" />
          Save draft
        </Button>
        <Button
          className="rounded-sm h-11 bg-[#0033CC] hover:bg-[#002299] text-white"
          onClick={() => save(true)}
          disabled={saving || readOnly}
          data-testid="submit-timesheet-btn"
        >
          <Send size={16} className="mr-2" />
          Submit for approval
        </Button>
      </div>
      {loading && <div className="mt-4 text-xs text-neutral-500">Loading…</div>}
    </div>
  );
}

function SummaryTile({ label, value, uppercase, testid }) {
  return (
    <div className="bg-white border border-[#E5E5E5] p-4 rounded-md" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-2">{label}</div>
      <div
        className={`font-display font-black text-2xl ${uppercase ? "uppercase" : ""} ${
          uppercase ? "" : "font-mono-num"
        }`}
      >
        {value}
      </div>
    </div>
  );
}
